// web/src/store/phone/_zitch.ts —— 内部：zitch WSS 解析 + 自愈冷却 + 挂断上限 + 事件/回显/会话清理
// 拆自 web/src/store/phone.ts 的同名分区（内容原样搬运）；由 web/src/store/phone.ts 以 export * 聚合，导出面对调用方不变。

import { Message } from '@arco-design/web-react';
import { setAssistHold } from '../../api/calls';
import { getFsDomain, getSysSettings } from '../../api/sysSettings';
import { requestNotifyPermission, startRingAlert, stopRingAlert } from '../../utils/ringAlert';
import { createZitchAgent, type ZitchAgent, type ZitchEvent } from '../../lib/zitchAgent';

import { INITIAL } from './_model';
import { bindSession, captureCdrBaseline, cleanupSession, matchCustomer } from './_session';
import { rt } from './_state';
import { usePhoneStore } from './store';

/**
 * 解析 zitch 软电话 WSS 地址：优先 `sys_setting.zitch_wss_url`（管理台「呼叫与媒体」可改，换域名/端口免重建 web），
 * 取不到回空串，由 zitchAgent 回落构建期 VITE_ZITCH_WSS / 页面默认。每次注册实时取——管理员改完重注册即生效。
 */
export async function resolveZitchWss(): Promise<string> {
  try {
    return (await getSysSettings()).zitch_wss_url?.trim() || '';
  } catch {
    return '';
  }
}
/** 软电话自愈冷却（毫秒） */
export const HEAL_COOLDOWN_MS = 30000;

/** 外呼「接通即自动挂断」判定上限（毫秒）：接通后超过此时长被对端/系统结束视为正常结束，不补 0 回显。
 *  实测「播报请在外地手机号前面加0 后挂断」的通话时长约 6~9s，故上限放宽到 20s 以覆盖该场景。 */

export async function teardownZitch() {
  rt.zitch?.unregister();
  rt.zitch = null;
  rt.zitchExt = '';
}

/** zitch 呼入/呼出置位（对齐 SIP bindSession 的 store 状态部分） */
export function bindZitchDirection(direction: 'in' | 'out', remote: string) {
  usePhoneStore.setState((p) => ({
    ...p,
    status: direction === 'in' ? 'ringing-in' : 'ringing-out',
    remoteNumber: remote,
    direction,
    muted: false,
    held: false,
    startedAt: undefined,
    customer: null,
    // 换一通：上一通解析出的客户号码作废（与 SIP bindSession 同口径）
    customerPhone: '',
    displayName: '',
    transferredOut: false,
    consultPhase: '',
    consultDest: '',
    invite: null,
    inviteTakeover: null,
  }));
  void matchCustomer(remote);
  void captureCdrBaseline();
  if (direction === 'in') {
    // 呼入：清空外呼补 0 跟踪（归属下一通外呼），坐席主动挂断标记复位
    rt.lastOutboundDial = '';
    rt.activeOutboundSeq = 0;
    rt.manualHangup = false;
    startRingAlert(remote);
  }
}

/**
 * 系统/对端自动结束外呼时的收尾。
 *
 * 原「若为外地号则回显补 0 号码」依赖号码属性（号段库），该能力已随「号码资源」页退役：
 * 现在只做状态复位，外地号补 0 由坐席按提示手工处理。
 */
export function scheduleOutboundEcho(_elapsedMs: number | undefined) {
  rt.lastOutboundDial = '';
  rt.manualHangup = false;
}

/** zitch 通话结束复位（customer/customerPhone/cdrBaseline 保留，与 SIP cleanupSession 语义一致） */
function cleanupZitchSession() {
  stopRingAlert();
  const prev = usePhoneStore.getState();
  const elapsed = prev.startedAt ? Math.max(0, Date.now() - prev.startedAt) : undefined;
  usePhoneStore.setState((p) => ({
    ...INITIAL,
    extension: p.extension,
    engine: p.engine,
    customer: p.customer,
    customerPhone: p.customerPhone,
    displayName: p.displayName,
    cdrBaseline: p.cdrBaseline,
    // 盲转标记保留给消费方读取后再复位：cleanup 不得先行清掉，否则 BasicLayout
    // 无法区分「盲转结束」与「正常挂断」，转出场景会误弹服务小结
    transferredOut: p.transferredOut,
    // 客户腿结束时邀请卡片仍可能在等接听，不得被会话复位清掉
    invite: p.invite,
    inviteTakeover: p.inviteTakeover,
    remoteNumber: p.inviteTakeover?.from || (p.invite ? p.remoteNumber : ''),
    direction: p.inviteTakeover ? 'in' : p.invite ? p.direction : '',
    status: rt.zitch?.isConnected() ? 'registered' : p.status === 'error' ? 'error' : 'idle',
  }));
  scheduleOutboundEcho(elapsed);
}

/**
 * 交换引擎在 `state:error` 里回的失败原因是**英文自由文本**（协议未约定枚举，也可能是 SIP 状态码），
 * 直接透给坐席只会看到 `dial failed` 这类无信息量的英文。这里按可辨识片段给中文处置建议，
 * 认不出的一律落通用文案；引擎原文只进控制台供排障——新增/纠正文案改本表，勿在调用点另写分支。
 */
const DIAL_FAIL_TEXT_RULES: Array<[RegExp, string]> = [
  [/whitelist/i, '该号码不在外呼白名单内，已被阻止拨出'],
  [/blacklist|forbidden|denied|permission/i, '该号码被限制呼叫，请确认是否已拉黑或超出可拨范围'],
  [/no (such )?call|unknown call|call not found/i, '该通话已结束，无需操作'],
  [/trunk|no (available )?(route|gateway)/i, '外呼线路不可用（未配置网关或全部不可达），请联系管理员'],
  [/not (logged|registered)|unregistered|offline|no agent/i, '软电话未登录或已掉线，请先上线（或重新注册软电话）再拨'],
  [/busy|\b486\b/i, '对方正在通话中，请稍后再拨'],
  [/\b(408|480)\b|no ?answer|unreachable/i, '对方暂时无法接通（未接听/关机/不在服务区），请稍后再拨'],
  [/\b487\b|cancel/i, '呼叫已取消'],
  [/\b(403|404|488|503)\b|invalid|bad (number|dest)/i, '号码无效或外呼服务暂不可用，请核对号码后重试'],
  [/media|sdp|codec/i, '媒体协商失败（网络或编解码不支持），请重试'],
];

function dialFailText(reason?: string): string {
  const raw = (reason || '').trim();
  // 已是中文（含字母数字的中文句子）：原样展示，避免通用文案盖掉更具体的原因
  if (/[\u4e00-\u9fa5]/.test(raw)) return raw;
  for (const [re, text] of DIAL_FAIL_TEXT_RULES) {
    if (re.test(raw)) return text;
  }
  return '未能拨出，请核对号码后重试；若持续失败请联系管理员检查外呼线路';
}

/** zitch 事件 → 软电话状态机（与 SIP bindSession/cleanupSession 对齐） */
export function onZitchEvent(ev: ZitchEvent) {
  switch (ev.kind) {
    case 'registered':
      usePhoneStore.setState((p) =>
        p.status === 'registering' || p.status === 'error' ? { status: 'registered' } : p,
      );
      break;
    case 'login_err':
      if (ev.reason !== 'kicked') {
        usePhoneStore.setState({ status: 'error' });
      }
      break;
    case 'ringing-in':
      bindZitchDirection('in', ev.remote);
      break;
    case 'in-call':
      stopRingAlert();
      usePhoneStore.setState((p) => ({
        status: 'in-call',
        startedAt: Date.now(),
        ...(p.inviteTakeover
          ? { remoteNumber: p.inviteTakeover.from, direction: 'in' as const, inviteTakeover: null }
          : {}),
      }));
      break;
    case 'held': {
      const customerCall = rt.zitch?.currentCall();
      if (customerCall && ev.call && ev.call !== customerCall) break;
      usePhoneStore.setState({ held: ev.held });
      void setAssistHold(ev.held);
      break;
    }
    case 'consult-ringing':
      usePhoneStore.setState({ consultPhase: 'ringing' });
      break;
    case 'consult-up':
      usePhoneStore.setState({ consultPhase: 'up' });
      break;
    case 'consult-ended':
      // 完成转接已把 consultPhase 清掉：专家腿随后 ended 不得再 unhold 客户
      if (!usePhoneStore.getState().consultPhase) break;
      void rt.zitch?.unhold();
      usePhoneStore.setState({ consultPhase: '', consultDest: '', held: false });
      void setAssistHold(false);
      break;
    case 'consult-failed':
      if (ev.message) console.warn('[phone] 咨询失败原因（引擎原文）：', ev.message);
      Message.error(`邀请失败：${dialFailText(ev.message)}`);
      if (usePhoneStore.getState().consultPhase) void rt.zitch?.unhold();
      usePhoneStore.setState({ consultPhase: '', consultDest: '', held: false });
      void setAssistHold(false);
      break;
    case 'invite-in':
      usePhoneStore.setState({ invite: { call: ev.call, from: ev.from, note: ev.note } });
      break;
    case 'invite-ended':
      usePhoneStore.setState((p) => {
        const card = p.invite?.call === ev.call;
        const take = p.inviteTakeover?.call === ev.call;
        if (!card && !take) return p;
        // 邀请未升主路就结束：清接管；已不在通话中则不要把邀请号留在条上
        const clearBar = take && p.status !== 'in-call';
        return {
          invite: card ? null : p.invite,
          inviteTakeover: take ? null : p.inviteTakeover,
          ...(clearBar ? { remoteNumber: '', direction: '' as const } : {}),
        };
      });
      break;
    case 'ended':
      cleanupZitchSession();
      break;
    case 'ring-timeout':
      // 振铃超时被服务端回收：本机没接过这通电话，按「来电已取消」收尾（与 FS 侧 CANCEL 行为一致）
      Message.info('来电振铃超时，已转派其他坐席');
      cleanupZitchSession();
      break;
    case 'dial-failed':
      // 只把中文结论给坐席；引擎原文留控制台，便于据此补 DIAL_FAIL_TEXT_RULES
      if (ev.message) console.warn('[phone] 呼叫失败原因（引擎原文）：', ev.message);
      Message.error(`呼叫失败：${dialFailText(ev.message)}`);
      cleanupZitchSession();
      break;
    default:
      break;
  }
}
